import React, { useEffect, useState } from 'react';
import styled from 'styled-components';
import { CalendarRange, Store } from 'lucide-react';
import eventsApi from 'api/requests/events';
import breakpoints from 'theme/breakpoints';
import colors from 'theme/colors';
import { panelAccent } from 'theme/panelAccent';

export const DEFAULT_FILTERS = { period: 'all' };

const PERIODS = [
    { value: 'today', label: 'Hoje' },
    { value: '7d', label: '7 dias' },
    { value: '30d', label: '30 dias' },
    { value: '90d', label: '90 dias' },
    { value: 'all', label: 'Tudo' },
];

const CUSTOM = 'custom';

/**
 * Extrai a lista plana de eventos do payload agrupado de /events/my-events.
 * Rascunho fica de fora: não tem venda, só polui o seletor.
 *
 * O identificador é o slug — o EventResource não expõe id, e slug é a chave
 * de rota que o resto do painel já usa.
 */
function flattenEvents(payload) {
    return ['now', 'next', 'expired']
        .flatMap(group => payload?.[group]?.items?.data || [])
        .filter(event => event?.slug)
        .map(event => ({ slug: event.slug, name: event.name }));
}

export default function FinancialFilters({ filters, onChange }) {
    const [events, setEvents] = useState([]);
    const [customRange, setCustomRange] = useState({ start: '', end: '' });

    const isCustom = Boolean(filters?.start_date);
    const activePeriod = isCustom ? CUSTOM : (filters?.period || 'all');

    useEffect(() => {
        async function loadEvents() {
            try {
                const { data } = await eventsApi.myEvents();
                setEvents(flattenEvents(data?.data));
            } catch (error) {
                /* Sem a lista o seletor some e o resto dos filtros segue funcionando. */
            }
        }

        loadEvents();
    }, []);

    function selectPeriod(period) {
        if (period === CUSTOM) {
            setCustomRange({ start: '', end: '' });
            onChange({ ...filters, period: undefined, start_date: undefined, end_date: undefined, custom: true });
            return;
        }

        onChange({ ...filters, period, start_date: undefined, end_date: undefined, custom: false });
    }

    function changeCustomRange(field, value) {
        const nextRange = { ...customRange, [field]: value };
        setCustomRange(nextRange);

        // O backend exige as duas pontas; só dispara quando o intervalo fecha.
        if (nextRange.start && nextRange.end) {
            onChange({
                ...filters,
                period: undefined,
                start_date: nextRange.start,
                end_date: nextRange.end,
                custom: true,
            });
        }
    }

    function changeEvent(value) {
        onChange({ ...filters, event: value || undefined });
    }

    const showCustomInputs = activePeriod === CUSTOM || filters?.custom;

    return (
        <Container>
            <Group>
                <GroupLabel>
                    <CalendarRange size={14} />
                    <span>Período</span>
                </GroupLabel>

                <Chips>
                    {PERIODS.map(period => (
                        <Chip
                            key={period.value}
                            type="button"
                            selected={activePeriod === period.value}
                            onClick={() => selectPeriod(period.value)}
                        >
                            {period.label}
                        </Chip>
                    ))}
                    <Chip
                        type="button"
                        selected={activePeriod === CUSTOM}
                        onClick={() => selectPeriod(CUSTOM)}
                    >
                        Personalizado
                    </Chip>
                </Chips>

                {showCustomInputs && (
                    <RangeInputs>
                        <DateInput
                            type="date"
                            value={customRange.start}
                            max={customRange.end || undefined}
                            onChange={(e) => changeCustomRange('start', e.target.value)}
                            aria-label="Data inicial"
                        />
                        <RangeSeparator>até</RangeSeparator>
                        <DateInput
                            type="date"
                            value={customRange.end}
                            min={customRange.start || undefined}
                            onChange={(e) => changeCustomRange('end', e.target.value)}
                            aria-label="Data final"
                        />
                    </RangeInputs>
                )}
            </Group>

            {events.length > 0 && (
                <Group>
                    <GroupLabel>
                        <Store size={14} />
                        <span>Evento</span>
                    </GroupLabel>

                    <Select
                        value={filters?.event || ''}
                        onChange={(e) => changeEvent(e.target.value)}
                        aria-label="Filtrar por evento"
                    >
                        <option value="">Todos os eventos</option>
                        {events.map(event => (
                            <option key={event.slug} value={event.slug}>{event.name}</option>
                        ))}
                    </Select>
                </Group>
            )}

            <Note>
                Saldo disponível e total a receber não mudam com o filtro — são a
                posição de agora, não movimento do período.
            </Note>
        </Container>
    );
}

const Container = styled.div`
    display: flex;
    flex-direction: column;
    gap: 12px;
    background: ${colors.neutralLight};
    border: 1px solid ${colors.neutralDark};
    border-radius: 12px;
    padding: 16px;
    margin-top: 10px;

    @media screen and (min-width: ${breakpoints.lg}) {
        flex-direction: row;
        align-items: center;
        flex-wrap: wrap;
        gap: 20px;
    }
`;

const Group = styled.div`
    display: flex;
    flex-direction: column;
    gap: 8px;

    @media screen and (min-width: ${breakpoints.lg}) {
        flex-direction: row;
        align-items: center;
        gap: 10px;
    }
`;

const GroupLabel = styled.span`
    display: flex;
    align-items: center;
    gap: 6px;
    font-size: 12px;
    font-weight: 600;
    color: ${colors.night};
    white-space: nowrap;
`;

const Chips = styled.div`
    display: flex;
    flex-wrap: wrap;
    gap: 6px;
`;

const Chip = styled.button`
    padding: 6px 12px;
    border-radius: 50px;
    border: 1px solid ${({ selected }) => selected ? panelAccent(colors.secondaryPurple) : colors.neutralDark};
    background: ${({ selected }) => selected ? '#F3E8FF' : colors.neutralLight};
    color: ${({ selected }) => selected ? panelAccent(colors.secondaryPurple) : colors.night};
    font-weight: ${({ selected }) => selected ? '600' : '400'};
    font-size: 12px;
    font-family: inherit;
    cursor: pointer;
    white-space: nowrap;
    transition: all 0.15s ease;

    &:hover {
        background: ${({ selected }) => selected ? '#F3E8FF' : colors.neutral};
    }

    &:focus-visible {
        outline: 2px solid ${panelAccent(colors.occasionalPurple)};
        outline-offset: 2px;
    }
`;

const RangeInputs = styled.div`
    display: flex;
    align-items: center;
    gap: 8px;
`;

const RangeSeparator = styled.span`
    font-size: 12px;
    color: ${colors.nightLight};
`;

const DateInput = styled.input`
    padding: 6px 10px;
    border: 1px solid ${colors.neutralDark};
    border-radius: 8px;
    background: ${colors.neutralLight};
    color: ${colors.nightDark};
    font-size: 12px;
    font-family: inherit;

    &:focus-visible {
        outline: 2px solid ${panelAccent(colors.occasionalPurple)};
        outline-offset: -1px;
    }
`;

const Select = styled.select`
    padding: 7px 10px;
    border: 1px solid ${colors.neutralDark};
    border-radius: 8px;
    background: ${colors.neutralLight};
    color: ${colors.nightDark};
    font-size: 12px;
    font-family: inherit;
    cursor: pointer;
    max-width: 260px;

    &:focus-visible {
        outline: 2px solid ${panelAccent(colors.occasionalPurple)};
        outline-offset: -1px;
    }
`;

const Note = styled.span`
    font-size: 11px;
    color: ${colors.nightLight};
    line-height: 1.4;

    @media screen and (min-width: ${breakpoints.lg}) {
        margin-left: auto;
        max-width: 280px;
        text-align: right;
    }
`;
